import React, { ReactElement, Ref } from 'react';

import PropTypes from 'prop-types';

import { defaultLabelGetter } from '../../helpers/defaultGetters';
import { defaultRenderItems } from '../../helpers/defaultRenderers';
import useAutocompleteControl from '../../hooks/useAutocompleteControl';
import {
  ControlContainer,
  type ControlContainerAutocompleteProps,
  SearchInput,
} from '../../layout';
import {
  GenericItem,
  GetInputProps,
  GetSelectedItemProps,
  ItemToString,
} from '../../types';

export type MultiAutocompleteControlProps<TItem extends GenericItem> = Omit<
  ControlContainerAutocompleteProps<TItem>,
  'renderPlaceholder' | 'renderInput' | 'isAutocomplete'
> & {
  getInputProps?: GetInputProps;
  getSelectedItemProps: GetSelectedItemProps<TItem>;
  selectedItems: TItem[] | null;
  removeSelectedItem: (item: TItem) => void;
  itemToString?: ItemToString<TItem>;
  placeholder?: string;
  searchPlaceholder?: string;
  renderItems?: MultiControlRenderItems<TItem>;
  renderInput?: (
    props: React.ComponentProps<typeof SearchInput>,
  ) => React.ReactNode;
  renderPlaceholder?: React.ComponentProps<typeof ControlContainer>['children'];
  openMenu?: () => void;
  softLimit?: number;
  maxLength?: number;
  showCount?: boolean;
  countDisplayThreshold?: number;
};

type MultiControlRenderItems<TItem extends GenericItem> = (args: {
  getSelectedItemProps: GetSelectedItemProps<TItem>;
  isOpen: boolean;
  selectedItems: TItem[];
  removeSelectedItem: (item: TItem) => void;
  itemToString: ItemToString<TItem>;
  disabled?: boolean;
}) => React.ReactNode;

const MultiAutocompleteControlWrapped = <TItem extends GenericItem>(
  {
    selectedItems,
    isOpen,
    disabled,
    openMenu,
    getInputProps,
    itemToString = defaultLabelGetter,
    placeholder: placeholderProp,
    searchPlaceholder: searchPlaceholderProp,
    removeSelectedItem: _removeSelectedItem,
    getSelectedItemProps: _getSelectedItemProps,
    renderItems = defaultRenderItems,
    softLimit,
    maxLength,
    showCount,
    countDisplayThreshold,
    ...props
  }: MultiAutocompleteControlProps<TItem>,
  ref: Ref<HTMLDivElement>,
) => {
  const { containerProps, searchInputProps } = useAutocompleteControl({
    placeholderProp,
    searchPlaceholderProp,
    isOpen,
    disabled,
    openMenu,
    selectedItems,
  });

  const showItemCount =
    !isOpen && !!selectedItems?.length && selectedItems.length > 0;

  return (
    <ControlContainer ref={ref} {...containerProps} {...props}>
      {showItemCount &&
        renderItems({
          isOpen,
          selectedItems,
          getSelectedItemProps: _getSelectedItemProps,
          removeSelectedItem: _removeSelectedItem,
          itemToString,
          disabled,
        })}
      <SearchInput
        style={{ ...(showItemCount && { display: 'none' }) }}
        isOpen={isOpen}
        {...getInputProps?.(searchInputProps)}
        showIcon={isOpen}
        softLimit={softLimit}
        maxLength={maxLength}
        showCount={showCount}
        countDisplayThreshold={countDisplayThreshold}
      />
    </ControlContainer>
  );
};

const MultiAutocompleteControl = React.forwardRef(
  MultiAutocompleteControlWrapped,
);

MultiAutocompleteControl.displayName = 'MultiAutocompleteControl';

MultiAutocompleteControl.propTypes = {
  /** Function to open menu.  Provided by useMultiAutocomplete hook */
  openMenu: PropTypes.func.isRequired,
};

export default MultiAutocompleteControl as <T extends GenericItem>(
  p: MultiAutocompleteControlProps<T> & {
    ref?: Ref<HTMLDivElement>;
  },
) => ReactElement;
